import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from "react";
import { useBackendFlags } from "../utils/hooks/useBackendFlags";
import { getAqidCookie, getUidCookie } from "../utils/uid";
import type { ApiConfig } from "./ApiConfig";
import { useConfigsApi } from "./useConfigsApi";
import { useFeatureOverride } from "./internal/useFeatureOverride";
import { useExperimentOverride } from "./internal/useExperimentOverride";

const ApiConfigContext = createContext<ApiConfig>({
  type: "pending",
});

const uidCookie = getUidCookie();
const aqidCookie = getAqidCookie();
export function ApiConfigProvider(props: PropsWithChildren<{}>) {
  const featureOverride = useFeatureOverride();
  const experimentOverride = useExperimentOverride();
  const { backendFlags } = useBackendFlags();
  const configs = useConfigsApi((data) => ({
    uid: data.uid,
    aqid: data.aqid,
  }));

  const value: ApiConfig = useMemo(() => {
    if (configs.data && !configs.isPlaceholderData) {
      return {
        type: "resolved",
        uid: configs.data.uid,
        aqid: configs.data.aqid,
        backendFeatures: featureOverride,
        backendExperiments: experimentOverride,
        backendFlags,
      };
    } else {
      return {
        type: "pending",
        uid: uidCookie,
        aqid: aqidCookie,
        backendFeatures: featureOverride,
        backendExperiments: experimentOverride,
        backendFlags,
      };
    }
  }, [
    configs.data,
    configs.isPlaceholderData,
    featureOverride,
    experimentOverride,
    backendFlags,
  ]);

  return <ApiConfigContext value={value}>{props.children}</ApiConfigContext>;
}

export function useApiConfig(): ApiConfig {
  return useContext<ApiConfig>(ApiConfigContext);
}
